import { useState, useEffect, useRef, useMemo } from "react"

type Phase = "idle" | "queued" | "processing" | "complete"

export function useEstimatedProgress({
  isActive,
  serverProgress,
  estimatedDuration,
}: {
  isActive: boolean
  serverProgress?: number
  estimatedDuration: number
}) {
  const [progress, setProgress] = useState(0)
  const [phase, setPhase] = useState<Phase>("idle")
  const startTimeRef = useRef<number | null>(null)
  const rafRef = useRef<number | null>(null)
  const durationRef = useRef(estimatedDuration)
  const serverProgressRef = useRef(serverProgress)

  useEffect(() => {
    durationRef.current = estimatedDuration
  }, [estimatedDuration])

  useEffect(() => {
    serverProgressRef.current = serverProgress
  }, [serverProgress])

  useEffect(() => {
    if (!isActive) {
      // If we were previously active, transition to complete
      if (startTimeRef.current) {
        startTimeRef.current = null
        if (rafRef.current) {
          cancelAnimationFrame(rafRef.current)
          rafRef.current = null
        }
        queueMicrotask(() => {
          setProgress(1)
          setPhase("complete")
        })
      }
      return
    }

    // Starting — record start time and begin ticking
    startTimeRef.current = Date.now()
    queueMicrotask(() => {
      setProgress(0)
      setPhase("processing")
    })

    function tick() {
      if (!startTimeRef.current) return

      const elapsed = (Date.now() - startTimeRef.current) / 1000
      const estimated = 0.92 * (1 - Math.exp((-3 * elapsed) / durationRef.current))

      // Snap to server progress if higher
      const server = serverProgressRef.current
      const value = server != null && server > estimated ? server : estimated

      setProgress((prev) => Math.max(prev, value))
      rafRef.current = requestAnimationFrame(tick)
    }

    rafRef.current = requestAnimationFrame(tick)

    return () => {
      if (rafRef.current) {
        cancelAnimationFrame(rafRef.current)
        rafRef.current = null
      }
    }
  }, [isActive])

  // Update phase based on server progress
  const derivedPhase = useMemo((): Phase => {
    if (phase === "complete" || phase === "idle") return phase
    if (!isActive) return phase
    if (serverProgress === 0) return "queued"
    return "processing"
  }, [phase, isActive, serverProgress])

  // Reset phase from "complete" to "idle" after a brief hold
  useEffect(() => {
    if (phase !== "complete") return

    const timer = setTimeout(() => {
      setPhase("idle")
    }, 600)

    return () => clearTimeout(timer)
  }, [phase])

  return { progress, phase: derivedPhase }
}
